// Radio
// Features:
// - Radio mesh with a small dancing animation when turned on
// - Floating musical note sprites that pulse while the radio is on
// - Looping room sound track, gated by both radio state and global sound toggle

import { useEffect, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useTexture, Instances, Instance } from '@react-three/drei'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'
import gsap from 'gsap'

import { useStore } from '../../store/useStore'
import RoomSound from './RoomSound'

import noteTextureURL from '/textures/note.png'

export default function Radio({ nodes, material }) {
    const radioRef = useRef()
    const notesRef = useRef()
    const timelinesRef = useRef([])
    const switchAudioRef = useRef()

    const [turnedOn, setTurnedOn] = useState(true)
    const isSoundOn = useStore((state) => state.isSoundOn)

    // Textures
    const noteTexture = useTexture(noteTextureURL, (texture) => {
        texture.colorSpace = THREE.NoColorSpace
    })

    // Interaction: Click to toggle radio
    useEffect(() => {
        // Setup switch sound
        const audio = new Audio('/sounds/switch.mp3')
        audio.preload = 'auto'
        audio.volume = isSoundOn ? 0.1 : 0
        switchAudioRef.current = audio

        const onMouseDown = (e) => {
            if (e.button !== 0) return
            const state = useStore.getState()
            if (state.currentHitName === 'Radio' && state.isPointerLocked) {
                setTurnedOn((prev) => !prev)
                if (switchAudioRef.current && useStore.getState().isSoundOn) {
                    switchAudioRef.current.currentTime = 0
                    switchAudioRef.current.play().catch(() => {})
                }
            }
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => {
            window.removeEventListener('mousedown', onMouseDown)
            if (switchAudioRef.current) {
                switchAudioRef.current.pause()
                switchAudioRef.current = null
            }
        }
    }, [])

    // Update switch sound volume
    useEffect(() => {
        if (switchAudioRef.current) {
            switchAudioRef.current.volume = isSoundOn ? 0.1 : 0
        }
    }, [isSoundOn])

    // Animations (Breathing + Notes)
    useEffect(() => {
        if (!radioRef.current) return

        const obj = radioRef.current.scale
        const base = { x: 1, y: 1, z: 1 }

        // Clear previous animations
        gsap.killTweensOf(obj)
        timelinesRef.current.forEach((tl) => tl && tl.kill())
        timelinesRef.current = []

        if (turnedOn) {
            // Breathing
            gsap.set(obj, { x: base.x, y: base.y, z: base.z })
            gsap.to(obj, {
                x: base.x - 0.02,
                y: base.y + 0.02,
                z: base.z - 0.02,
                duration: 0.8,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1,
            })

            // Note animations
            if (notesRef.current) {
                notesRef.current.children.forEach((child, index) => {
                    const tl = gsap.timeline({ repeat: -1, delay: index })

                    // Float up
                    tl.fromTo(
                        child.position,
                        { y: 0 },
                        { y: 0.4, duration: 2, ease: 'linear' }
                    )

                    // Pulse scale
                    tl.fromTo(
                        child.scale,
                        { x: 0, y: 0, z: 0 },
                        {
                            x: 1,
                            y: 1,
                            z: 1,
                            duration: 1,
                            ease: 'power2.out',
                            yoyo: true,
                            repeat: 1,
                        },
                        0
                    )

                    timelinesRef.current.push(tl)
                })
            }
        } else {
            // Reset scale
            gsap.to(obj, {
                x: base.x,
                y: base.y,
                z: base.z,
                duration: 0.75,
                ease: 'power2.out',
            })
        }

        return () => {
            gsap.killTweensOf(obj)
            timelinesRef.current.forEach((tl) => tl && tl.kill())
        }
    }, [turnedOn])

    // Horizontal sine and billboard to camera for the floating notes
    useFrame((state) => {
        if (!notesRef.current || !turnedOn) return
        const { clock, camera } = state
        notesRef.current.children.forEach((child, index) => {
            child.position.x = Math.sin(clock.elapsedTime + index) * 0.2
            child.lookAt(camera.position)
        })
    })

    return (
        <group>
            <RigidBody
                colliders="cuboid"
                type="fixed"
                userData={{ name: 'Radio' }}
                position={[-2.657, 1.613, -0.114]}
                rotation={[0, 0.049, 0]}
            >
                <mesh
                    ref={radioRef}
                    geometry={nodes.room_radio.geometry}
                    material={material}
                />
            </RigidBody>

            {/* Floating Notes */}
            <group position={[-2.657, 2.113, -0.114]}>
                <Instances ref={notesRef} visible={turnedOn}>
                    <planeGeometry args={[0.2, 0.2]} />
                    <meshBasicMaterial
                        alphaMap={noteTexture}
                        transparent={true}
                        opacity={0.5}
                        depthWrite={false}
                    />
                    {Array(2)
                        .fill(null)
                        .map((_, i) => (
                            <Instance key={i} position={[0, 0, 0]} />
                        ))}
                </Instances>
            </group>

            <RoomSound
                url="/sounds/aloha.mp3"
                volume={turnedOn ? 0.2 : 0}
                position={[-2.657, 1.613, -0.114]}
            />
        </group>
    )
}
